import type { IMessage, IMessageReferenceData } from "../models/index.js";
import type { IEndlessRange } from "./endless-range.collaborator";
import type {
    IRemoteRangeRequestOptions,
    RemoteLoadingState,
    RemoteRequestDirection,
    RemoteRequestMode,
    RemoteRequestReason
} from "./remote-range-loader.collaborator";

export interface IEndlessScrollRenderOptions {
    scrollToBottom?: boolean;
    scrollToMessageUid?: string;
}

export interface IEndlessScrollCoordinatorContext {
    beginLatestRemoteStartup: () => void;
    isEndlessScrollEnabled: () => boolean;
    isRemoteEndlessScroll: () => boolean;
    getPageSize: () => number;
    getTotalCount: () => number;
    getRenderedRange: () => IEndlessRange;
    getLoadedRange: () => IEndlessRange;
    getLatestRange: (totalCount?: number) => IEndlessRange;
    getTargetRange: (targetIndex: number, totalCount?: number) => IEndlessRange;
    isLatestRenderedRange: (totalCount?: number) => boolean;
    isRangeLoaded: (startIndex: number, endIndex: number) => boolean;
    getMessageIndexById: (id: string | number) => number;
    requestRemoteRange: (options: IRemoteRangeRequestOptions) => void;
    renderRange: (startIndex: number, endIndex: number, options?: IEndlessScrollRenderOptions) => void;
    scrollToBottom: () => void;
    refreshScrollState: () => void;
    scrollToMessage: (uid: string) => boolean;
    prependLocalRange: (startIndex: number, endIndex: number) => void;
    appendLocalRange: (startIndex: number, endIndex: number) => void;
    updateEndlessState: () => void;
}

export class EndlessScrollCoordinator {
    private context: IEndlessScrollCoordinatorContext;

    constructor(context: IEndlessScrollCoordinatorContext) {
        this.context = context;
    }

    requestLatestRemoteStartupRange(): void {
        this.context.beginLatestRemoteStartup();
        this._requestLatestRange(0, this.context.getPageSize(), "scroll-to-bottom", {
            scrollToBottom: true
        });
    }

    prepareLatestMessages(): void {
        if (!this.context.isEndlessScrollEnabled()) {
            return;
        }

        const totalCount = this.context.getTotalCount();

        if (!totalCount) {
            return;
        }

        const latestRange = this.context.getLatestRange(totalCount);

        if (this.context.isRemoteEndlessScroll() && !this.context.isRangeLoaded(latestRange.startIndex, latestRange.endIndex)) {
            this._requestLatestRange(latestRange.startIndex, latestRange.endIndex, "scroll-to-bottom", {
                scrollToBottom: true
            });
            return;
        }

        if (!this.context.isLatestRenderedRange(totalCount)) {
            this.context.renderRange(latestRange.startIndex, latestRange.endIndex, { scrollToBottom: true });
            return;
        }

        this.context.scrollToBottom();
        this.context.refreshScrollState();
    }

    navigateToReferencedMessage(id: string | number, message: IMessageReferenceData | null, loadedMessage: IMessage | null): void {
        if (loadedMessage?.uid && this.context.scrollToMessage(loadedMessage.uid)) {
            return;
        }

        if (!this.context.isEndlessScrollEnabled()) {
            return;
        }

        const targetIndex = this.context.getMessageIndexById(id);

        if (targetIndex < 0) {
            if (!this.context.isRemoteEndlessScroll()) {
                return;
            }

            this._requestLatestRange(0, this.context.getPageSize(), "reference-jump", {
                targetMessageId: id
            });
            return;
        }

        const targetRange = this.context.getTargetRange(targetIndex, this.context.getTotalCount());

        this.context.renderRange(targetRange.startIndex, targetRange.endIndex, {
            scrollToMessageUid: loadedMessage?.uid || message?.uid
        });
    }

    loadOlderMessages(): void {
        const renderedRange = this.context.getRenderedRange();

        if (this.context.isRemoteEndlessScroll()) {
            const loadedRange = this.context.getLoadedRange();

            if (loadedRange.startIndex <= 0) {
                this.context.updateEndlessState();
                return;
            }

            const pageSize = this.context.getPageSize();

            this._requestAdjacentRange(
                Math.max(0, loadedRange.startIndex - pageSize),
                loadedRange.startIndex,
                "older",
                "prepend",
                "older"
            );
            return;
        }

        if (renderedRange.startIndex > 0) {
            const pageSize = this.context.getPageSize();

            this.context.prependLocalRange(Math.max(0, renderedRange.startIndex - pageSize), renderedRange.startIndex);
            return;
        }

        this.context.updateEndlessState();
    }

    loadNewerMessages(): void {
        const renderedRange = this.context.getRenderedRange();
        const totalCount = this.context.getTotalCount();

        if (this.context.isRemoteEndlessScroll()) {
            const loadedRange = this.context.getLoadedRange();

            if (loadedRange.endIndex >= totalCount) {
                this.context.updateEndlessState();
                return;
            }

            const pageSize = this.context.getPageSize();

            this._requestAdjacentRange(
                loadedRange.endIndex,
                Math.min(totalCount, loadedRange.endIndex + pageSize),
                "newer",
                "append",
                "newer"
            );
            return;
        }

        if (renderedRange.endIndex >= totalCount) {
            this.context.updateEndlessState();
            return;
        }

        const pageSize = this.context.getPageSize();

        this.context.appendLocalRange(renderedRange.endIndex, Math.min(totalCount, renderedRange.endIndex + pageSize));
    }

    private _requestLatestRange(
        startIndex: number,
        endIndex: number,
        reason: RemoteRequestReason,
        extras: Partial<Pick<IRemoteRangeRequestOptions, "scrollToBottom" | "targetMessageId">> = {}
    ): void {
        this.context.requestRemoteRange(this._createRequest(startIndex, endIndex, "latest", reason, "replace", "all", extras));
    }

    private _requestAdjacentRange(
        startIndex: number,
        endIndex: number,
        direction: RemoteRequestDirection,
        mode: RemoteRequestMode,
        loadingState: RemoteLoadingState
    ): void {
        this.context.requestRemoteRange(this._createRequest(startIndex, endIndex, direction, "edge-scroll", mode, loadingState));
    }

    private _createRequest(
        startIndex: number,
        endIndex: number,
        direction: RemoteRequestDirection,
        reason: RemoteRequestReason,
        mode: RemoteRequestMode,
        loadingState: RemoteLoadingState,
        extras: Partial<Pick<IRemoteRangeRequestOptions, "scrollToBottom" | "targetMessageId">> = {}
    ): IRemoteRangeRequestOptions {
        return {
            startIndex,
            endIndex,
            direction,
            reason,
            mode,
            loadingState,
            ...extras
        };
    }
}